import { Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { accessScopeQuery, findTeam } from "../../api/session";
import { UnlockCards } from "../../platform/achievements/UnlockCard";
import { InvitationsBanner } from "../../platform/InvitationsForYou";
import { readPref, writePref } from "../../platform/prefs";
import { StudioHeader } from "./StudioHeader";
import { StudioNav, useStudioSection } from "./StudioNav";
import classes from "./studio2.module.css";

/**
 * `home2`'s frame, its team and brand the URL's. Simple has no nav (spec
 * ui-perf/pro-studio-redesign, "Simple and Pro Studio").
 */
export function Studio2Shell() {
  const { teamId, brandId } = useParams({ from: "/signed-in/teams/$teamId/brands/$brandId/home2" });
  const simple = useStudioSection() === null;
  return (
    <ProShell teamId={teamId} brandId={brandId} simple={simple}>
      <Outlet />
    </ProShell>
  );
}

interface ProShellProps {
  teamId: string;
  brandId: string;
  /** Simple's frame: the header alone, with no nav. */
  simple: boolean;
  children: ReactNode;
}

/**
 * The Pro Studio redesign's frame (spec ui-perf/pro-studio-redesign): the header across the top,
 * the nav on the left and the page in a rounded panel that scrolls on its own. The team's layout
 * keeps its stream and media cookies and draws no current shell (`staticData.ownFrame`). `app/`
 * draws the staff pages in it too (spec ui-perf/admin-panel-pro-simple).
 */
export function ProShell({ teamId, brandId, simple, children }: ProShellProps) {
  const role = useQuery({
    ...accessScopeQuery(),
    select: (scope) => findTeam(scope, teamId)?.role,
  }).data;
  const phone = useMediaQuery("(max-width: 48em)") === true;
  const [drawer, setDrawer] = useState(false);
  // Open with labels, or the compact rail, as the current shell's sidebar, and remembered.
  const [open, setOpen] = useState(() => readPref("proNavOpen"));
  const toggle = () => {
    writePref("proNavOpen", !open);
    setOpen(!open);
  };
  // The team's layout renders this only for a team in the scope.
  if (role === undefined) return null;
  return (
    <div
      className={classes.shell}
      data-bare={simple || undefined}
      data-compact={simple || phone || open ? undefined : true}
    >
      <StudioHeader
        teamId={teamId}
        brandId={brandId}
        role={role}
        simple={simple}
        drawer={simple || !phone ? undefined : { opened: drawer, toggle: () => setDrawer(!drawer) }}
        nav={simple || phone ? undefined : { open, toggle }}
      />
      {/* Beside the header, as Classic's shell mounts them; a card opens Pro's own Achievements tab
          (spec ui-perf/pro-unlock-cards). */}
      <UnlockCards teamId={teamId} brandId={brandId} />
      {/* On a phone the nav is the drawer's alone, as the current shell's sidebar is. */}
      {simple || phone ? null : (
        <nav aria-label="Main" className={classes.nav} data-compact={open ? undefined : true}>
          <StudioNav teamId={teamId} brandId={brandId} open={open} />
        </nav>
      )}
      {/* The router restores and resets this element's scroll (app/router.tsx). */}
      <main id="main" className={classes.main} data-scroll-restoration-id="main">
        <InvitationsBanner />
        {children}
      </main>
      {simple || !phone ? null : (
        <Drawer.Root opened={drawer} onClose={() => setDrawer(false)} size={260}>
          <Drawer.Overlay backgroundOpacity={0.5} blur={2} />
          <Drawer.Content aria-label="Navigation">
            <Drawer.Body className={classes.drawerNav}>
              <nav aria-label="Main">
                <StudioNav teamId={teamId} brandId={brandId} onNavigate={() => setDrawer(false)} />
              </nav>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </div>
  );
}
